/* eslint-disable no-underscore-dangle */
// This should be used for all requests instead of importing directly form 'axios'.

import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { API_REFRESH_TOKEN, API_BASE_URL } from 'constants/appConstants';

import { redirectToLogin as redirectToLoginAction } from 'containers/App/actions';
import { logout } from 'containers/Logout/actions';

import { isAuthFrontendCutoverEnabled } from 'utils/flags/isAuthFrontendCutoverEnabled';
import { buildLoginRedirectUrl } from 'utils/buildLoginRedirectUrl';
import { history } from 'utils/history';
import { currentAccountId } from 'utils/currentAccountId';
import { getAnonymousUserid } from 'utils/anonymousUserId';
import { store } from '../createStore';
import auth, { getUser } from '../auth';

type TCallback = () => void;

let isRefreshing = false;
const refreshSubscribers: TCallback[] = [];

export const cancelSource = axios.CancelToken.source();

const notAuthenticated = (response: AxiosResponse) =>
  response ? response.status === 401 : false;

const subscribeToRefresh = (cb: TCallback) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = () => {
  refreshSubscribers.map((cb) => cb());
  refreshSubscribers.length = 0; // Empty subscribers array after all callbacks are executed
};

const getSearchParam = (urlString: string, searchParam: string) => {
  try {
    const url = new URL(urlString);
    const result = url.searchParams.get(searchParam);

    if (result) {
      return result;
    }

    return '';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Error parsing url');
    // eslint-disable-next-line no-console
    console.error(error);
    return '';
  }
};

function isEmbedded() {
  const currentUrl = document.location.href;
  return getSearchParam(currentUrl, 'isEmbedded');
}

const redirectToLogin = () => {
  const currentUrl = document.location.href;
  auth.cleanUp();
  store.dispatch(redirectToLoginAction(currentUrl));
  store.dispatch(logout());

  // If URL has an email query param in it then pass it down to Login explicitly
  const email = getSearchParam(currentUrl, 'email');
  // Is app being embedded in an iframe.
  const isAppEmbedded = isEmbedded();

  if (isAppEmbedded) {
    history.push('/pendingLogin');
    return;
  }

  const currentPath = window.location.href.replace(window.location.origin, '');

  /** redirectUrl is used to indicate to the login component which url to redirect user to after logging in.
      This is needed since we're reloading /login route from the server so we no longer have access to the redux value of global.redirectedFrom
      which was previously used to redirect the user after logging in. */
  const redirectPath = currentPath !== '/logout' ? currentPath : '';

  const url = buildLoginRedirectUrl({
    useNewAuthFrontend: isAuthFrontendCutoverEnabled(),
    email,
    redirectPath,
  });

  // Use window.location.href below to force page to reload /login route from server instead of client-side routing to it.
  // This stops embedded/iframed app from rendering login page which prevents clickjacking attacks on the login page.
  // This is because the GET doc request for the html file of the /login route provides response header of
  // 'Content-Security-Policy=frame-ancestors 'none''. Whereas GET doc requests for the html file of all other routes do not provide this restriction.
  // If we revert this to client-side routing (e.g. history.push(url)) we get no such CSP protection from click-jacking attacks on the login page.
  window.location.href = url;
};

export const refreshAccessToken = () =>
  new Promise<void>((resolve, reject) => {
    axios({
      baseURL: API_BASE_URL,
      method: 'post',
      url: API_REFRESH_TOKEN,
    })
      .then(() => resolve())
      .catch(() => {
        reject(new Error('Token has expired'));
      });
  });

const createAxiosInstance = () => {
  const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache', // IE 11 specific
      Expires: -1, // IE 11 specific
      // https://support.microsoft.com/en-au/help/234067/how-to-prevent-caching-in-internet-explorer
    },
  });

  axiosInstance.interceptors.request.use(
    (config) => {
      const originalConfig = config;
      if (originalConfig.headers) {
        const hasUser = !!getUser();
        if (!hasUser) {
          originalConfig.headers['x-anonymous-id'] = getAnonymousUserid();
        }

        const accountId = currentAccountId.get();
        if (accountId) {
          originalConfig.headers['x-checkbox-account'] = accountId;
        }
      }
      return Promise.resolve(originalConfig);
    },
    (error) => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const { config } = error;
      const response: AxiosResponse | undefined = error.response;

      /**
       * While we deploy a new version of the application, the `/api/*` endpoints will
       * return a 503 with the body 'System updating'.
       *
       * We also have a maintenance page at `/` during this time and because the frontend has
       * already loaded for users mid-session, we need a means to redirect them to the maintenance
       * page.
       *
       * Being React w/ React-Router, further routes may or may not already have loaded, so
       * its much easier for us to force the browser to navigate outside of the SPA.
       */
      if (response?.data === 'System updating') {
        // Use navigator instead of react-router
        window.location.replace('/');
      }

      if (response && notAuthenticated(response)) {
        /*
        Attempt to refresh accessToken if the request is not a retry
        Second unsuccessful retry will redirect to login
      */

        if (!config._retry) {
          config._retry = true;

          if (!isRefreshing) {
            isRefreshing = true;

            refreshAccessToken()
              .then(() => {
                isRefreshing = false;
                onRefreshed();
              })
              .catch(() => {
                isRefreshing = false;
                redirectToLogin();
              });
          }

          const retryOrig = new Promise((resolve) => {
            subscribeToRefresh(() => {
              resolve(axiosInstance(config));
            });
          });

          return retryOrig;
        }
        // Do not need to redirect to logout as session is effectively does not exist at this stage
        redirectToLogin();

        // This is here jic if workflow changes
        // Currently will not get executed as redirectToLogin();
        return Promise.reject(response);
      }

      /*
    Resolving instead of rejecting to keep handling errors inside try blocks
    TODO: move handling of all errors >= 400 into catch block
    then change this to Promise.reject(response);
     */
      return Promise.resolve(response);
    }
  );

  return axiosInstance;
};

const axiosSingleton = (() => {
  let instance;

  return {
    instance: (() => {
      if (!instance) {
        instance = createAxiosInstance();
      }

      return instance;
    })(),
  };
})();

export default axiosSingleton.instance;
